import { useCallback } from 'react';
import { ArrowUpRight, Check, CopyIcon, Settings } from 'lucide-react';
import { useParams } from 'react-router';

import { copyText } from '@components/patterns';
import { Badge, useDrawer } from '@components/primitives';
import { toast } from '@vault';

import { AppObject } from '@api/types';
import { AsvRegistrationDrawer } from '@components/features/Admin/AsvRegistrationDrawer';
import { AppIcon } from '@components/features/Apps/AppIcon';
import { useAsvConfigQuery } from '@components/features/AsvScans/queries';
import { isAsvEnrolled } from '@components/features/AsvScans/utils';
import { Command } from '@components/features/CommandMenu';
import { NavigationDropdown } from '@components/features/Layout/Navigation/Dropdown';
import { useAppsQuery } from '@queries/app';

import css from './styles.module.css';

export function AppActions({
  app,
  children,
}: {
  app: AppObject;
  children: React.ReactNode;
}) {
  const { teamUuid } = useParams();
  const asvRegistrationDrawer = useDrawer();

  const { data: apps } = useAppsQuery(teamUuid ?? '');
  const otherApps = apps?.filter((a) => a.uuid !== app.uuid) ?? [];

  const asvConfigQuery = useAsvConfigQuery(app.teamUuid, app.uuid);
  const copyAsvCloneId = useCallback(async () => {
    if (!asvConfigQuery.data) return;
    const organizationId = asvConfigQuery.data.organization.id;
    await copyText(organizationId);
    toast(`Copied "${organizationId}`, {
      id: `copy-asv-clone-id-${organizationId}`,
    });
  }, [asvConfigQuery.data]);

  return (
    <>
      <AsvRegistrationDrawer state={asvRegistrationDrawer} app={app} />

      <NavigationDropdown trigger={children}>
        <NavigationDropdown.Section>
          <NavigationDropdown.Item
            icon={<AppIcon app={app} />}
            label={<span className={css.teamName}>{app.name}</span>}
          />

          <NavigationDropdown.Item
            icon={<ArrowUpRight />}
            label="Open app"
            to={`/${teamUuid}/${app.uuid}`}
          />
        </NavigationDropdown.Section>

        <NavigationDropdown.Section label="ASV Scans">
          <NavigationDropdown.Item
            icon={<Settings />}
            label="Configure"
            onClick={asvRegistrationDrawer.open}
            suffix={
              asvConfigQuery.isLoading ||
              !asvConfigQuery.isSuccess ? null : !isAsvEnrolled(
                  asvConfigQuery.data
                ) ? (
                <Badge size="sm" variant="warning">
                  Misconfigured
                </Badge>
              ) : (
                <Badge size="sm" variant="success">
                  <Check />
                  Active
                </Badge>
              )
            }
          />

          {!asvConfigQuery.isError && (
            <NavigationDropdown.Item
              icon={<CopyIcon />}
              label="Copy organization ID"
              disabled={!asvConfigQuery.data}
              onClick={copyAsvCloneId}
            />
          )}
        </NavigationDropdown.Section>

        {otherApps.length > 0 && (
          <NavigationDropdown.Section label="Switch app">
            {otherApps.map((app) => (
              <NavigationDropdown.Item
                key={app.uuid}
                icon={<AppIcon app={app} />}
                label={app.name}
                to={`/admin/${teamUuid}/${app.uuid}`}
              />
            ))}
          </NavigationDropdown.Section>
        )}
      </NavigationDropdown>

      <Command
        type="route"
        id="app-open"
        label="Open app"
        icon={<ArrowUpRight />}
        context="app"
        group={app.name}
        href={`/${teamUuid}/${app.uuid}`}
      />

      <Command
        type="action"
        id="app-configure-asv"
        label="Configure ASV"
        icon={<Settings />}
        context="app"
        group={app.name}
        onSelect={asvRegistrationDrawer.open}
      />

      {!asvConfigQuery.isError && (
        <Command
          type="action"
          id="app-copy-asv-clone-id"
          label="Copy organization ID"
          sublabel="ASV Scans"
          icon={<CopyIcon />}
          context="app"
          group={app.name}
          onSelect={copyAsvCloneId}
          keywords={['asv', 'scans', 'clone']}
        />
      )}

      {otherApps?.map((app) => (
        <Command
          key={app.uuid}
          type="route"
          id={app.uuid}
          label={app.name}
          icon={<AppIcon app={app} />}
          href={`/admin/${teamUuid}/${app.uuid}`}
          group="Apps"
          context="team"
          priority={-1}
        />
      ))}
    </>
  );
}
